<template>
  <Teleport to="body">
    <div 
      v-if="isVisible" 
      class="homepage-modal-overlay"
      :style="modalStyles.overlay"
      @click.self="closeModal"
    >
      <div
        class="homepage-modal-container"
        :class="{ 'modal-appear': isVisible }"
        :style="modalStyles.container"
      >
        <!-- Close Button -->
        <button 
          class="homepage-modal-close" 
          :style="modalStyles.closeButton"
          @click="closeModal"
          aria-label="Κλείσιμο"
        >
          <i class="bi bi-x-lg"></i>
        </button>

        <!-- Modal Content (Widgets) -->
        <div class="homepage-modal-content" :style="modalStyles.content">
          <PageBuilderWidgets 
            v-if="modalSettings.modal_widgets && modalSettings.modal_widgets.length > 0"
            :widgets="modalSettings.modal_widgets"
            :is-editable="false"
          />
          <div v-else class="text-center p-5">
            <i class="bi bi-inbox display-4 text-muted"></i>
            <p class="text-muted mt-3">Δεν υπάρχει περιεχόμενο στο modal</p>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import PageBuilderWidgets from '@/components/admin/pagebuilder/PageBuilderWidgets.vue'
import type { Widget } from '@/types/pagebuilder'
import { getHomepageModalStyles } from '@/utils/homepageModalStyles'

interface ModalSettings {
  modal_enabled: string
  modal_delay: number
  modal_frequency: string
  modal_widgets: Widget[]
  modal_bg_color?: string
  modal_bg_opacity?: number | string
  modal_text_color?: string
  modal_overlay_color?: string
  modal_overlay_opacity?: number | string
  modal_overlay_blur?: number | string
  modal_padding?: number | string
  modal_border_radius?: number | string
  modal_max_width?: number | string
}

const isVisible = ref(false)
const modalSettings = ref<ModalSettings>({
  modal_enabled: 'false',
  modal_delay: 1000,
  modal_frequency: 'once_per_session',
  modal_widgets: [],
  modal_bg_color: '#ffffff',
  modal_bg_opacity: 100,
  modal_text_color: '#333333',
  modal_overlay_color: '#000000',
  modal_overlay_opacity: 70,
  modal_overlay_blur: 4,
  modal_padding: 0,
  modal_border_radius: 20,
  modal_max_width: 900,
})

const modalStyles = computed(() => getHomepageModalStyles(modalSettings.value))

// Load settings
const loadModalSettings = async () => {
  try {
    const response = await axios.get('/backend/api/settings.php')
    
    if (response.data.success && response.data.data && response.data.data.content) {
      const content = response.data.data.content
      
      // Parse modal_widgets - it might be a string or already an array
      let widgets = content.modal_widgets || []
      if (typeof widgets === 'string') {
        try {
          widgets = JSON.parse(widgets)
        } catch (e) {
          console.error('Failed to parse modal_widgets:', e)
          widgets = []
        }
      }
      
      modalSettings.value = {
        modal_enabled: content.modal_enabled || 'false',
        modal_delay: parseInt(content.modal_delay) || 1000,
        modal_frequency: content.modal_frequency || 'once_per_session',
        modal_widgets: Array.isArray(widgets) ? widgets : [],
        modal_bg_color: content.modal_bg_color || '#ffffff',
        modal_bg_opacity: content.modal_bg_opacity ?? 100,
        modal_text_color: content.modal_text_color || '#333333',
        modal_overlay_color: content.modal_overlay_color || '#000000',
        modal_overlay_opacity: content.modal_overlay_opacity ?? 70,
        modal_overlay_blur: content.modal_overlay_blur ?? 4,
        modal_padding: content.modal_padding ?? 0,
        modal_border_radius: content.modal_border_radius ?? 20,
        modal_max_width: content.modal_max_width ?? 900,
      }
      
      // Check if modal should be shown
      if (shouldShowModal()) {
        showModal()
      }
    } else {
      console.warn('⚠️ Invalid API response structure:', response.data)
    }
  } catch (error) {
    console.error('❌ Error loading modal settings:', error)
  }
}

// Check if modal should be shown based on frequency
const shouldShowModal = (): boolean => {
  if (modalSettings.value.modal_enabled !== 'true') {
    return false
  }

  const frequency = modalSettings.value.modal_frequency
  const now = Date.now()
  
  switch (frequency) {
    case 'always':
      return true
      
    case 'once_per_session':
      const sessionKey = 'homepage_modal_shown_session'
      if (sessionStorage.getItem(sessionKey)) {
        return false
      }
      sessionStorage.setItem(sessionKey, 'true')
      return true
      
    case 'once_per_day':
      const dayKey = 'homepage_modal_shown_day'
      const lastShownDay = localStorage.getItem(dayKey)
      const today = new Date().toDateString()
      
      if (lastShownDay === today) {
        return false
      }
      localStorage.setItem(dayKey, today)
      return true
      
    case 'once_per_week':
      const weekKey = 'homepage_modal_shown_week'
      const lastShownWeek = localStorage.getItem(weekKey)
      const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000)
      
      if (lastShownWeek && parseInt(lastShownWeek) > oneWeekAgo) {
        return false
      }
      localStorage.setItem(weekKey, now.toString())
      return true
      
    default:
      return true
  }
}

// Show modal with delay
const showModal = () => {
  setTimeout(() => {
    isVisible.value = true
    document.body.style.overflow = 'hidden'
  }, modalSettings.value.modal_delay)
}

// Close modal
const closeModal = () => {
  isVisible.value = false
  document.body.style.overflow = ''
}

// Close on Escape key
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isVisible.value) {
    closeModal()
  }
}

onMounted(() => {
  // Wait for page to fully load before checking modal
  window.addEventListener('load', () => {
    // Small delay to ensure loader animation completes
    setTimeout(() => {
      loadModalSettings()
    }, 500)
  })
  
  document.addEventListener('keydown', handleEscapeKey)
})
</script>

<style scoped>
.homepage-modal-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 1rem;
  animation: fadeIn 0.3s ease;
  box-sizing: border-box;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.homepage-modal-container {
  width: 100%;
  max-height: min(90vh, 900px);
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
  transform: scale(0.94);
  opacity: 0;
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease;
  /* background / radius / overflow come from inline styles */
  isolation: isolate;
}

.homepage-modal-container.modal-appear {
  transform: scale(1);
  opacity: 1;
}

.homepage-modal-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(15, 23, 42, 0.45);
  color: #fff !important;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  transition: background 0.2s ease, transform 0.2s ease;
  font-size: 1.05rem;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}

.homepage-modal-close:hover {
  background: rgba(220, 53, 69, 0.95);
  color: #fff !important;
  transform: rotate(90deg) scale(1.06);
}

.homepage-modal-content {
  flex: 1 1 auto;
  min-height: 0;
  max-height: min(90vh, 900px);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* Kill inherited white cards / gaps from page-builder */
  background-clip: padding-box;
}

.homepage-modal-content :deep(.page-builder-widgets) {
  min-height: 0;
  background: transparent !important;
}

.homepage-modal-content :deep(.widgets-container) {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 0;
  background: transparent !important;
}

.homepage-modal-content :deep(.widget-item) {
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent !important;
  box-shadow: none !important;
  overflow: hidden;
}

.homepage-modal-content :deep(.widget-content) {
  background: transparent !important;
}

/* Image widgets fill the modal width — no side gutters */
.homepage-modal-content :deep(.image-widget) {
  width: 100% !important;
  max-width: 100% !important;
  display: block;
}

.homepage-modal-content :deep(.image-widget .image-container),
.homepage-modal-content :deep(.image-widget .image-link) {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
}

.homepage-modal-content :deep(.image-widget .widget-image) {
  display: block;
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
}

/* Hide widget controls in modal */
.homepage-modal-content :deep(.widget-controls) {
  display: none !important;
}

/* Responsive */
@media (max-width: 768px) {
  .homepage-modal-overlay {
    padding: 0.65rem;
  }

  .homepage-modal-container {
    max-width: 100%;
    max-height: 88vh;
  }

  .homepage-modal-content {
    max-height: 88vh;
  }

  .homepage-modal-close {
    width: 36px;
    height: 36px;
    top: 0.5rem;
    right: 0.5rem;
  }
}

/* Scrollbar — track follows modal bg, not white */
.homepage-modal-content::-webkit-scrollbar {
  width: 8px;
}

.homepage-modal-content::-webkit-scrollbar-track {
  background: transparent;
}

.homepage-modal-content::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.35);
  border-radius: 10px;
}

.homepage-modal-content::-webkit-scrollbar-thumb:hover {
  background: rgba(15, 23, 42, 0.5);
}
</style>
